import { spider, type SpiderRules } from '@cardengine/shared';
import { useStore } from '../store.js';

interface SpiderRulesEditorProps {
  rules: SpiderRules;
  editable: boolean;
}

const SUIT_OPTIONS: { value: SpiderRules['suitCount']; label: string; hint: string }[] = [
  { value: 1, label: 'One suit', hint: 'Spades only. Every sequence is in suit, so runs always move whole.' },
  { value: 2, label: 'Two suits', hint: 'Spades and hearts. Half your neat columns will not travel.' },
  { value: 4, label: 'Four suits', hint: 'The full pack twice over. Properly hard.' },
];

interface ToggleProps {
  label: string;
  hint: string;
  checked: boolean;
  disabled: boolean;
  onChange: (value: boolean) => void;
}

function Toggle({ label, hint, checked, disabled, onChange }: ToggleProps) {
  return (
    <label className={`rule ${disabled ? 'rule--locked' : ''}`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="rule__body">
        <span className="rule__label">{label}</span>
        <span className="rule__hint">{hint}</span>
      </span>
    </label>
  );
}

/**
 * Spider's settings, in the same shape as the other three editors.
 *
 * One knob that matters and one that softens the endgame. The suit count is
 * offered as a select as well as a preset row because it is the setting people
 * actually come here to change, and burying the real difficulty behind a
 * preset name would be coy.
 */
export function SpiderRulesEditor({ rules, editable }: SpiderRulesEditorProps) {
  const updateRules = useStore((s) => s.updateRules);

  const set = <K extends keyof SpiderRules>(key: K, value: SpiderRules[K]) => {
    if (editable) updateRules({ [key]: value } as Partial<SpiderRules>);
  };

  const activePreset = spider.RULE_PRESETS.find(
    (preset) => JSON.stringify(preset.rules) === JSON.stringify(rules),
  );

  return (
    <section className="rules" aria-label="Spider settings">
      <header className="rules__header">
        <h2>Settings</h2>
      </header>

      <div className="presets">
        <span className="presets__label">Preset</span>
        <div className="presets__row">
          {spider.RULE_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              className={`presets__button ${activePreset?.id === preset.id ? 'is-active' : ''}`}
              disabled={!editable}
              onClick={() => editable && updateRules(preset.rules)}
              title={preset.description}
            >
              {preset.name}
            </button>
          ))}
        </div>
        <p className="presets__description">
          {activePreset ? activePreset.description : 'Custom settings.'}
        </p>
      </div>

      <div className="rules__ranks">
        <label className="rule rule--choice">
          <span className="rule__label">Suits</span>
          <select
            value={rules.suitCount}
            disabled={!editable}
            onChange={(event) => {
              const value = Number(event.target.value);
              // Only 1, 2 and 4 divide two packs into eight equal runs. The
              // server rejects anything else, so the select never offers it.
              if (value === 1 || value === 2 || value === 4) set('suitCount', value);
            }}
          >
            {SUIT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <span className="rule__hint">
            {SUIT_OPTIONS.find((o) => o.value === rules.suitCount)?.hint}
          </span>
        </label>
      </div>

      <div className="rules__toggles">
        <Toggle
          label="Deal with a column empty"
          hint="Off is standard, and it is what gives an empty column its price — you cannot bank a gap and keep turning cards. On makes the endgame considerably kinder."
          checked={rules.allowDealWithEmptyColumn}
          disabled={!editable}
          onChange={(value) => set('allowDealWithEmptyColumn', value)}
        />
      </div>
    </section>
  );
}
